import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

function App() {
  const [fio, setFio] = useState('');
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const [copiedFioOriginal, setCopiedFioOriginal] = useState(false);
  const [copiedFioTranslit, setCopiedFioTranslit] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  useEffect(() => {
    if (address.length >= 3) {
      const timer = setTimeout(() => {
        searchAddress(address);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
      setSelectedIndex(0);
    }
  }, [address]);

  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post('/api/suggest', {
        query
      });
      
      setSuggestions(response.data.suggestions || []);
      setSelectedIndex(0);
      setDebugInfo(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    setAddress(suggestion.value + ' ');
    setSelectedAddress(suggestion);
    setSuggestions([]);
    setSelectedIndex(0);
  };

  const handleKeyDown = (e) => {
    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectSuggestion(suggestions[selectedIndex]);
    } else if (e.key === 'Tab' && suggestions.length > 0) {
      e.preventDefault();
      selectSuggestion(suggestions[0]);
    }
  };

  const transliterationMap = {
    'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Е': 'E', 'Ё': 'E',
    'Ж': 'ZH', 'З': 'Z', 'И': 'I', 'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M',
    'Н': 'N', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T', 'У': 'U',
    'Ф': 'F', 'Х': 'KH', 'Ц': 'TS', 'Ч': 'CH', 'Ш': 'SH', 'Щ': 'SHCH',
    'Ъ': '', 'Ы': 'Y', 'Ь': '', 'Э': 'E', 'Ю': 'YU', 'Я': 'YA',
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'e',
    'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'kh', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'щ': 'shch',
    'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya'
  };

  const transliterate = (text) => {
    let result = '';
    for (let char of text) {
      if (transliterationMap.hasOwnProperty(char)) {
        result += transliterationMap[char];
      } else {
        result += char;
      }
    }
    return result.toUpperCase();
  };

  const capitalizeWords = (text) => {
    return text.split(' ')
      .map(word => {
        if (!word) return '';
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'fio-original') {
        setCopiedFioOriginal(true);
        setTimeout(() => setCopiedFioOriginal(false), 2000);
      } else if (type === 'fio-translit') {
        setCopiedFioTranslit(true);
        setTimeout(() => setCopiedFioTranslit(false), 2000);
      } else if (type === 'all') {
        setCopiedAll(true);
        setTimeout(() => setCopiedAll(false), 2000);
      }
    } catch (err) {
      console.error('Ошибка копирования:', err);
    }
  };

  const getFioCapitalized = () => {
    return capitalizeWords(fio.trim());
  };

  const getFioTransliterated = () => {
    const lines = getFormattedOutput().split('\n');
    return lines[0] || '';
  };

  const getFormattedOutput = () => {
    if (!fio && !selectedAddress) return '';

    const lines = [];
    
    if (fio) {
      lines.push(transliterate(fio));
    }
    
    if (selectedAddress && selectedAddress.data) {
      const data = selectedAddress.data;
      const hasAreaAndSettlement = data.area_with_type && data.settlement_with_type && !data.city_with_type;
      
      if (hasAreaAndSettlement) {
        const line2Parts = [];
        if (data.postal_code) line2Parts.push(data.postal_code);
        if (data.region_with_type) line2Parts.push(transliterate(data.region_with_type) + ',');
        if (line2Parts.length > 0) lines.push(line2Parts.join(', '));
        
        const line3Parts = [];
        if (data.area_with_type) line3Parts.push(transliterate(data.area_with_type));
        if (data.settlement_with_type) line3Parts.push(transliterate(data.settlement_with_type));
        if (line3Parts.length > 0) lines.push(line3Parts.join(', '));
        
        const line4Parts = [];
        if (data.street_with_type) line4Parts.push(transliterate(data.street_with_type));
        if (data.house) line4Parts.push(`D. ${data.house}`);
        if (data.block) line4Parts.push(`KORP. ${data.block}`);
        if (data.flat) line4Parts.push(`KV. ${data.flat}`);
        line4Parts.push('RUSSIA');
        lines.push(line4Parts.join(', '));
      } else {
        const line2Parts = [];
        if (data.postal_code) line2Parts.push(data.postal_code);
        if (data.region_with_type) line2Parts.push(transliterate(data.region_with_type) + ',');
        if (line2Parts.length > 0) lines.push(line2Parts.join(', '));
        
        const line3Parts = [];
        if (data.area_with_type) line3Parts.push(transliterate(data.area_with_type));
        if (data.city_with_type) {
          line3Parts.push(transliterate(data.city_with_type));
        } else if (data.settlement_with_type) {
          line3Parts.push(transliterate(data.settlement_with_type));
        }
        if (data.street_with_type) line3Parts.push(transliterate(data.street_with_type));
        if (line3Parts.length > 0) lines.push(line3Parts.join(', '));
        
        const line4Parts = [];
        if (data.house) line4Parts.push(`D. ${data.house}`);
        if (data.block) line4Parts.push(`KORP. ${data.block}`);
        if (data.flat) line4Parts.push(`KV. ${data.flat}`);
        if (line4Parts.length > 0 || lines.length > 1) {
          line4Parts.push('RUSSIA');
          lines.push(line4Parts.join(', '));
        }
      }
    }
    
    return lines.join('\n');
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#F1F5F9',
      padding: '20px 10px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          padding: '20px',
          background: 'rgba(30, 41, 59, 0.5)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          border: '1px solid rgba(148, 163, 184, 0.1)',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <h1 style={{ 
              margin: '0 0 5px 0',
              fontSize: 'clamp(20px, 4vw, 28px)',
              fontWeight: '600',
              background: 'linear-gradient(135deg, #60A5FA 0%, #A78BFA 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Транслитерация адресов
            </h1>
            <p style={{ margin: 0, color: '#94A3B8', fontSize: 'clamp(12px, 2vw, 14px)' }}>
              Автоматическое преобразование русских адресов в латиницу
            </p>
          </div>
          <span style={{ 
            background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
            color: 'white', 
            padding: '8px 20px', 
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
          }}>
            v1.0
          </span>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))',
          gap: '20px'
        }}>
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            backdropFilter: 'blur(10px)',
            padding: '20px',
            borderRadius: '16px',
            border: '1px solid rgba(148, 163, 184, 0.1)'
          }}>
            <h2 style={{ 
              margin: '0 0 20px 0', 
              fontSize: 'clamp(16px, 3vw, 18px)', 
              fontWeight: '600',
              color: '#F1F5F9'
            }}>
              Ввод данных
            </h2>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '8px',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <label style={{ 
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#94A3B8'
                }}>
                  ФИО
                </label>
                <button
                  onClick={() => copyToClipboard(getFioCapitalized(), 'fio-original')}
                  disabled={!fio.trim()}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: fio.trim() ? 'pointer' : 'not-allowed',
                    background: fio.trim() ? 'rgba(139, 92, 246, 0.2)' : 'rgba(100, 116, 139, 0.1)',
                    color: fio.trim() ? '#A78BFA' : '#64748B',
                    border: '1px solid',
                    borderColor: fio.trim() ? 'rgba(139, 92, 246, 0.3)' : 'rgba(100, 116, 139, 0.2)',
                    borderRadius: '8px',
                    transition: 'all 0.2s'
                  }}
                >
                  {copiedFioOriginal ? '✓ Скопировано' : '📋 Копировать'}
                </button>
              </div>
              <input
                type="text"
                value={fio}
                onChange={(e) => setFio(e.target.value)}
                placeholder="Иванов Иван Иванович"
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  fontSize: '16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: '10px',
                  color: '#F1F5F9',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82F6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {fio.trim() && (
                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748B' }}>
                  Будет скопировано: <span style={{ color: '#94A3B8' }}>{getFioCapitalized()}</span>
                </p>
              )}
            </div>
            
            <div style={{ marginBottom: '20px', position: 'relative' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '13px',
                fontWeight: '500',
                color: '#94A3B8'
              }}>
                Адрес <span style={{ color: '#64748B' }}>(Tab/Enter для выбора)</span>
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Начните вводить адрес..."
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  fontSize: '16px',
                  background: 'rgba(15, 23, 42, 0.6)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: '10px',
                  color: '#F1F5F9',
                  outline: 'none',
                  transition: 'all 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3B82F6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(148, 163, 184, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {loading && (
                <div style={{ 
                  color: '#94A3B8', 
                  marginTop: '8px', 
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid #3B82F6',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                  Поиск...
                </div>
              )}
              
              {suggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: 0,
                  right: 0,
                  background: 'rgba(30, 41, 59, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: '12px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
                  zIndex: 1000
                }}>
                  {suggestions.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => selectSuggestion(item)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: index < suggestions.length - 1 ? '1px solid rgba(148, 163, 184, 0.1)' : 'none',
                        background: index === selectedIndex ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <div style={{ fontWeight: '500', color: '#F1F5F9', fontSize: '14px' }}>
                        {item.value}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
                        Индекс: {item.data.postal_code || 'не найден'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedAddress && (
              <div style={{ 
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                padding: '16px', 
                borderRadius: '12px',
                marginTop: '20px'
              }}>
                <h3 style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#10B981'
                }}>
                  ✓ Выбранный адрес
                </h3>
                <div style={{ fontSize: '13px', lineHeight: '1.8', color: '#94A3B8' }}>
                  <div><strong style={{ color: '#CBD5E1' }}>Полный:</strong> {selectedAddress.unrestricted_value}</div>
                  <div><strong style={{ color: '#CBD5E1' }}>Индекс:</strong> {selectedAddress.data.postal_code || 'N/A'}</div>
                  <div><strong style={{ color: '#CBD5E1' }}>Регион:</strong> {selectedAddress.data.region_with_type}</div>
                  {selectedAddress.data.area_with_type && (
                    <div><strong style={{ color: '#CBD5E1' }}>Район:</strong> {selectedAddress.data.area_with_type}</div>
                  )}
                  <div><strong style={{ color: '#CBD5E1' }}>Город:</strong> {selectedAddress.data.city_with_type || selectedAddress.data.settlement_with_type || 'N/A'}</div>
                  <div><strong style={{ color: '#CBD5E1' }}>Улица:</strong> {selectedAddress.data.street_with_type || 'N/A'}</div>
                  <div><strong style={{ color: '#CBD5E1' }}>Дом:</strong> {selectedAddress.data.house || 'N/A'}</div>
                  {selectedAddress.data.block && (
                    <div><strong style={{ color: '#CBD5E1' }}>Корпус:</strong> {selectedAddress.data.block}</div>
                  )}
                  {selectedAddress.data.flat && (
                    <div><strong style={{ color: '#CBD5E1' }}>Квартира:</strong> {selectedAddress.data.flat}</div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div style={{
            background: 'rgba(30, 41, 59, 0.5)',
            backdropFilter: 'blur(10px)',
            padding: '20px',
            borderRadius: '16px',
            border: '1px solid rgba(148, 163, 184, 0.1)'
          }}>
            <h2 style={{ 
              margin: '0 0 20px 0', 
              fontSize: 'clamp(16px, 3vw, 18px)', 
              fontWeight: '600',
              color: '#F1F5F9'
            }}>
              Результат
            </h2>
            <div style={{
              background: 'rgba(15, 23, 42, 0.6)',
              padding: '20px',
              borderRadius: '12px',
              minHeight: '180px',
              fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", monospace',
              whiteSpace: 'pre-wrap',
              fontSize: '14px',
              lineHeight: '1.8',
              color: '#E2E8F0',
              border: '1px solid rgba(148, 163, 184, 0.1)',
              overflowX: 'auto'
            }}>
              {getFormattedOutput() || (
                <span style={{ color: '#64748B' }}>Заполните ФИО и выберите адрес...</span>
              )}
            </div>
            
            {getFormattedOutput() && (
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                marginTop: '20px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => copyToClipboard(getFioTransliterated(), 'fio-translit')}
                  style={{
                    flex: '1',
                    minWidth: '140px',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#60A5FA',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: '10px',
                    transition: 'all 0.2s'
                  }}
                >
                  {copiedFioTranslit ? '✓ Скопировано' : '📋 ФИО'}
                </button>
                <button
                  onClick={() => copyToClipboard(getFormattedOutput(), 'all')}
                  style={{
                    flex: '1',
                    minWidth: '140px',
                    padding: '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  {copiedAll ? '✓ Скопировано' : '📋 Всё'}
                </button>
              </div>
            )}
          </div>
        </div>
        
        {debugInfo && (
          <button
            onClick={() => setShowDebug(!showDebug)}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              background: 'rgba(100, 116, 139, 0.2)',
              color: '#94A3B8',
              border: '1px solid rgba(100, 116, 139, 0.3)',
              borderRadius: '10px',
              transition: 'all 0.2s'
            }}
          >
            {showDebug ? '🔽 Скрыть Debug' : '🔼 Показать Debug'}
          </button>
        )}
        
        {debugInfo && showDebug && (
          <div style={{ 
            marginTop: '20px',
            background: 'rgba(30, 41, 59, 0.5)',
            backdropFilter: 'blur(10px)',
            padding: '20px',
            borderRadius: '16px',
            border: '1px solid rgba(148, 163, 184, 0.1)'
          }}>
            <h2 style={{ 
              margin: '0 0 15px 0', 
              fontSize: '16px', 
              fontWeight: '600',
              color: '#F1F5F9'
            }}>
              Debug Info (Raw JSON)
            </h2>
            <pre style={{
              background: 'rgba(15, 23, 42, 0.8)',
              color: '#E2E8F0',
              padding: '20px',
              borderRadius: '12px',
              overflow: 'auto',
              maxHeight: '400px',
              fontSize: '12px',
              lineHeight: '1.6',
              border: '1px solid rgba(148, 163, 184, 0.1)'
            }}>
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        )}
      </div>
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.3);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: rgba(148, 163, 184, 0.3);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.5);
        }
      `}</style>
    </div>
  );
}

export default App;